// Mock data for the website
// Developer model
export interface Developer {
  id: string
  name: string
  title: string
  bio: string
  skills: string[]
  openTo: string[]
  location: string
  hourlyRate: number
  links: {
    github?: string
    linkedin?: string
    twitter?: string
    website?: string
  }
  approvalDate: string
  isFreelancer: boolean
  avatar: string
  status: 'available' | 'busy' | 'unavailable'
}
// Blog Post model
export interface BlogPost {
  id: string
  slug: string
  title: string
  excerpt: string
  content: string
  author: {
    name: string
    avatar: string
  }
  date: string
  readingTime: number
  categories: string[]
  featuredImage: string
}
// Partner model
export interface Partner {
  id: string
  name: string
  logo: string
  description: string
  website: string
}
// Sponsor model
export interface Sponsor {
  id: string
  name: string
  logo: string
  tier: 'gold' | 'silver' | 'bronze'
  description: string
  website: string
}
// Job Listing model
export interface JobListing {
  id: string
  title: string
  company: {
    name: string
    logo: string
    location: string
  }
  description: string
  requirements: string[]
  responsibilities: string[]
  location: string
  locationType: 'remote' | 'onsite' | 'hybrid'
  employmentType: 'full-time' | 'part-time' | 'contract' | 'freelance'
  experienceLevel: 'entry' | 'mid' | 'senior' | 'lead'
  salaryRange: {
    min: number
    max: number
    currency: string
  }
  skills: string[]
  postedDate: string
  applicationUrl: string
  featured: boolean
}
// Mock Developers
export const developers: Developer[] = [
  {
    id: '1',
    name: 'Alex Johnson',
    title: 'Senior Laravel Developer',
    bio: '10+ years of experience building web applications with Laravel and Vue.js. Passionate about clean code and performance optimization.',
    skills: ['Laravel', 'PHP', 'Vue.js', 'MySQL', 'Redis', 'AWS'],
    openTo: ['Full-time', 'Contract'],
    location: 'San Francisco, CA',
    hourlyRate: 85,
    links: {
      github: 'https://github.com/alexj',
      linkedin: 'https://linkedin.com/in/alexj',
      twitter: 'https://twitter.com/alexj',
      website: 'https://alexjohnson.dev',
    },
    approvalDate: '2023-05-15',
    isFreelancer: true,
    avatar: 'https://randomuser.me/api/portraits/men/1.jpg',
    status: 'available',
  },
  {
    id: '2',
    name: 'Sarah Williams',
    title: 'Full Stack Developer',
    bio: 'Full stack developer with expertise in Laravel, React, and Node.js. I love building scalable web applications and mentoring junior developers.',
    skills: ['Laravel', 'React', 'Node.js', 'PostgreSQL', 'Docker', 'TDD'],
    openTo: ['Full-time', 'Remote'],
    location: 'Toronto, Canada',
    hourlyRate: 75,
    links: {
      github: 'https://github.com/sarahw',
      linkedin: 'https://linkedin.com/in/sarahw',
    },
    approvalDate: '2023-06-22',
    isFreelancer: false,
    avatar: 'https://randomuser.me/api/portraits/women/2.jpg',
    status: 'busy',
  },
  {
    id: '3',
    name: 'Michael Chen',
    title: 'Backend Developer',
    bio: 'Specialized in building high-performance APIs with Laravel and optimizing database queries. Always looking to learn new technologies.',
    skills: ['Laravel', 'PHP', 'API Design', 'MySQL', 'Redis', 'Elasticsearch'],
    openTo: ['Contract', 'Freelance'],
    location: 'Berlin, Germany',
    hourlyRate: 70,
    links: {
      github: 'https://github.com/michaelc',
      website: 'https://michaelchen.dev',
    },
    approvalDate: '2023-04-10',
    isFreelancer: true,
    avatar: 'https://randomuser.me/api/portraits/men/3.jpg',
    status: 'available',
  },
  {
    id: '4',
    name: 'Emily Rodriguez',
    title: 'Laravel Developer & UI Designer',
    bio: 'Combining backend development skills with a passion for UI/UX design. I create beautiful, functional web applications that users love.',
    skills: [
      'Laravel',
      'PHP',
      'UI Design',
      'Tailwind CSS',
      'Alpine.js',
      'Figma',
    ],
    openTo: ['Full-time', 'Part-time'],
    location: 'Madrid, Spain',
    hourlyRate: 65,
    links: {
      github: 'https://github.com/emilyr',
      twitter: 'https://twitter.com/emilyr',
      website: 'https://emilyrodriguez.design',
    },
    approvalDate: '2023-07-05',
    isFreelancer: false,
    avatar: 'https://randomuser.me/api/portraits/women/4.jpg',
    status: 'unavailable',
  },
  {
    id: '5',
    name: 'David Kim',
    title: 'DevOps & Laravel Specialist',
    bio: 'Expert in Laravel development and DevOps practices. I help teams implement CI/CD pipelines and optimize their development workflows.',
    skills: [
      'Laravel',
      'PHP',
      'DevOps',
      'Docker',
      'Kubernetes',
      'AWS',
      'CI/CD',
    ],
    openTo: ['Consulting', 'Contract'],
    location: 'Seoul, South Korea',
    hourlyRate: 90,
    links: {
      github: 'https://github.com/davidk',
      linkedin: 'https://linkedin.com/in/davidk',
    },
    approvalDate: '2023-03-18',
    isFreelancer: true,
    avatar: 'https://randomuser.me/api/portraits/men/5.jpg',
    status: 'available',
  },
  {
    id: '6',
    name: 'Olivia Taylor',
    title: 'Laravel & Vue.js Developer',
    bio: 'Full stack developer specializing in Laravel and Vue.js. I enjoy building interactive web applications with a focus on user experience.',
    skills: ['Laravel', 'PHP', 'Vue.js', 'JavaScript', 'MySQL', 'Tailwind CSS'],
    openTo: ['Remote', 'Relocation'],
    location: 'Melbourne, Australia',
    hourlyRate: 80,
    links: {
      github: 'https://github.com/oliviat',
      linkedin: 'https://linkedin.com/in/oliviat',
      website: 'https://oliviataylor.dev',
    },
    approvalDate: '2023-08-12',
    isFreelancer: false,
    avatar: 'https://randomuser.me/api/portraits/women/6.jpg',
    status: 'busy',
  },
]
// Mock Blog Posts
export const blogPosts: BlogPost[] = [
  {
    id: '1',
    slug: 'laravel-10-new-features',
    title: 'Laravel 10: Exploring the New Features',
    excerpt:
      "Laravel 10 brings exciting new features and improvements. Let's take a deep dive into what's new and how it can improve your development workflow.",
    content:
      'Lorem ipsum dolor sit amet, consectetur adipiscing elit. Nullam euismod, nisl eget aliquam ultricies, nunc nisl aliquet nunc, quis aliquam nisl nunc eu nisl. Nullam euismod, nisl eget aliquam ultricies, nunc nisl aliquet nunc, quis aliquam nisl nunc eu nisl.',
    author: {
      name: 'John Doe',
      avatar: 'https://randomuser.me/api/portraits/men/11.jpg',
    },
    date: '2023-08-15',
    readingTime: 8,
    categories: ['Laravel', 'PHP', 'Web Development'],
    featuredImage:
      'https://images.unsplash.com/photo-1556075798-4825dfaaf498?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2070&q=80',
  },
  {
    id: '2',
    slug: 'optimizing-laravel-performance',
    title: '10 Tips for Optimizing Laravel Application Performance',
    excerpt:
      'Performance is crucial for user experience. Discover practical tips to optimize your Laravel application and make it blazing fast.',
    content:
      'Lorem ipsum dolor sit amet, consectetur adipiscing elit. Nullam euismod, nisl eget aliquam ultricies, nunc nisl aliquet nunc, quis aliquam nisl nunc eu nisl. Nullam euismod, nisl eget aliquam ultricies, nunc nisl aliquet nunc, quis aliquam nisl nunc eu nisl.',
    author: {
      name: 'Jane Smith',
      avatar: 'https://randomuser.me/api/portraits/women/12.jpg',
    },
    date: '2023-07-28',
    readingTime: 12,
    categories: ['Laravel', 'Performance', 'Optimization'],
    featuredImage:
      'https://images.unsplash.com/photo-1460925895917-afdab827c52f?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2015&q=80',
  },
  {
    id: '3',
    slug: 'laravel-livewire-tutorial',
    title: 'Building Dynamic Interfaces with Laravel Livewire',
    excerpt:
      'Laravel Livewire allows you to build dynamic interfaces without writing JavaScript. Learn how to leverage this powerful tool in your projects.',
    content:
      'Lorem ipsum dolor sit amet, consectetur adipiscing elit. Nullam euismod, nisl eget aliquam ultricies, nunc nisl aliquet nunc, quis aliquam nisl nunc eu nisl. Nullam euismod, nisl eget aliquam ultricies, nunc nisl aliquet nunc, quis aliquam nisl nunc eu nisl.',
    author: {
      name: 'Michael Brown',
      avatar: 'https://randomuser.me/api/portraits/men/13.jpg',
    },
    date: '2023-06-15',
    readingTime: 15,
    categories: ['Laravel', 'Livewire', 'Frontend'],
    featuredImage:
      'https://images.unsplash.com/photo-1555066931-4365d14bab8c?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2070&q=80',
  },
]
// Mock Partners
export const partners: Partner[] = [
  {
    id: '1',
    name: 'Acme Inc',
    logo: 'https://via.placeholder.com/150',
    description: 'Leading provider of web development tools and services.',
    website: 'https://acme.example.com',
  },
  {
    id: '2',
    name: 'TechHub',
    logo: 'https://via.placeholder.com/150',
    description:
      'Global technology community that supports startups and developers.',
    website: 'https://techhub.example.com',
  },
  {
    id: '3',
    name: 'DevStack',
    logo: 'https://via.placeholder.com/150',
    description: 'Modern development platform for teams of all sizes.',
    website: 'https://devstack.example.com',
  },
  {
    id: '4',
    name: 'CodeMasters',
    logo: 'https://via.placeholder.com/150',
    description:
      'Educational platform for learning programming and web development.',
    website: 'https://codemasters.example.com',
  },
]
// Mock Sponsors
export const sponsors: Sponsor[] = [
  {
    id: '1',
    name: 'CloudHost',
    logo: 'https://via.placeholder.com/150',
    tier: 'gold',
    description: 'Cloud hosting solutions optimized for Laravel applications.',
    website: 'https://cloudhost.example.com',
  },
  {
    id: '2',
    name: 'DevTools Pro',
    logo: 'https://via.placeholder.com/150',
    tier: 'gold',
    description: 'Professional development tools for modern web applications.',
    website: 'https://devtoolspro.example.com',
  },
  {
    id: '3',
    name: 'ServerStack',
    logo: 'https://via.placeholder.com/150',
    tier: 'silver',
    description: 'Managed server solutions for high-performance applications.',
    website: 'https://serverstack.example.com',
  },
  {
    id: '4',
    name: 'CodeBoost',
    logo: 'https://via.placeholder.com/150',
    tier: 'silver',
    description: 'Performance optimization tools for Laravel applications.',
    website: 'https://codeboost.example.com',
  },
  {
    id: '5',
    name: 'DevSecOps',
    logo: 'https://via.placeholder.com/150',
    tier: 'bronze',
    description: 'Security solutions for modern web applications.',
    website: 'https://devsecops.example.com',
  },
]
// Mock Job Listings
export const jobListings: JobListing[] = [
  {
    id: '1',
    title: 'Senior Laravel Developer',
    company: {
      name: 'TechInnovate',
      logo: 'https://via.placeholder.com/100?text=TechInnovate',
      location: 'San Francisco, CA',
    },
    description:
      'We are looking for a Senior Laravel Developer to join our growing team. You will be responsible for developing and maintaining web applications using Laravel and other related technologies.',
    requirements: [
      'At least 5 years of experience with PHP and Laravel',
      'Strong knowledge of front-end technologies (HTML, CSS, JavaScript)',
      'Experience with Vue.js or similar JavaScript frameworks',
      'Understanding of RESTful APIs and web services',
      'Experience with database design and optimization (MySQL)',
      'Knowledge of DevOps practices and CI/CD pipelines',
    ],
    responsibilities: [
      'Develop and maintain web applications using Laravel',
      'Write clean, maintainable, and efficient code',
      'Collaborate with the team to design and implement new features',
      'Troubleshoot and fix issues in existing applications',
      'Optimize applications for maximum speed and scalability',
      'Participate in code reviews and help maintain code quality',
    ],
    location: 'San Francisco, CA',
    locationType: 'hybrid',
    employmentType: 'full-time',
    experienceLevel: 'senior',
    salaryRange: {
      min: 120000,
      max: 150000,
      currency: 'USD',
    },
    skills: ['Laravel', 'PHP', 'Vue.js', 'MySQL', 'REST APIs', 'Git', 'Docker'],
    postedDate: '2023-09-15',
    applicationUrl:
      'https://techinnovate.example.com/careers/senior-laravel-developer',
    featured: true,
  },
  {
    id: '2',
    title: 'Full Stack Laravel Developer',
    company: {
      name: 'WebSolutions',
      logo: 'https://via.placeholder.com/100?text=WebSolutions',
      location: 'New York, NY',
    },
    description:
      'WebSolutions is seeking a talented Full Stack Laravel Developer to help build and maintain modern web applications for our clients. The ideal candidate will have strong experience with both back-end and front-end technologies.',
    requirements: [
      'At least 3 years of experience with Laravel and PHP',
      'Proficiency in JavaScript, HTML, and CSS',
      'Experience with front-end frameworks like Vue.js or React',
      'Knowledge of database systems like MySQL or PostgreSQL',
      'Familiarity with Git version control',
      'Good understanding of web application security',
    ],
    responsibilities: [
      'Develop web applications using Laravel and modern JavaScript frameworks',
      'Implement responsive designs and ensure cross-browser compatibility',
      'Optimize applications for performance and scalability',
      'Work with the design team to implement UI/UX features',
      'Collaborate with other developers on the team',
      'Maintain and improve existing applications',
    ],
    location: 'New York, NY',
    locationType: 'remote',
    employmentType: 'full-time',
    experienceLevel: 'mid',
    salaryRange: {
      min: 90000,
      max: 120000,
      currency: 'USD',
    },
    skills: [
      'Laravel',
      'PHP',
      'JavaScript',
      'Vue.js',
      'React',
      'MySQL',
      'HTML/CSS',
    ],
    postedDate: '2023-09-10',
    applicationUrl:
      'https://websolutions.example.com/careers/full-stack-laravel-developer',
    featured: false,
  },
  {
    id: '3',
    title: 'Laravel Backend Developer',
    company: {
      name: 'DataFlow Systems',
      logo: 'https://via.placeholder.com/100?text=DataFlow',
      location: 'Austin, TX',
    },
    description:
      'DataFlow Systems is looking for a Laravel Backend Developer to help build and maintain our data processing applications. You will work on creating APIs, optimizing database queries, and ensuring the security and performance of our systems.',
    requirements: [
      'At least 2 years of experience with Laravel and PHP',
      'Strong knowledge of database design and optimization',
      'Experience with RESTful API development',
      'Understanding of server management and deployment',
      'Familiarity with Redis or other caching systems',
      'Knowledge of unit testing and TDD',
    ],
    responsibilities: [
      'Design and implement RESTful APIs using Laravel',
      'Optimize database queries and ensure data integrity',
      'Implement caching strategies to improve performance',
      'Write unit tests to ensure code quality',
      'Collaborate with front-end developers to integrate APIs',
      'Monitor and troubleshoot production issues',
    ],
    location: 'Austin, TX',
    locationType: 'onsite',
    employmentType: 'full-time',
    experienceLevel: 'mid',
    salaryRange: {
      min: 85000,
      max: 110000,
      currency: 'USD',
    },
    skills: [
      'Laravel',
      'PHP',
      'MySQL',
      'Redis',
      'API Development',
      'Unit Testing',
    ],
    postedDate: '2023-09-05',
    applicationUrl:
      'https://dataflow.example.com/careers/laravel-backend-developer',
    featured: false,
  },
  {
    id: '4',
    title: 'Laravel/Vue.js Developer',
    company: {
      name: 'CreativeMinds',
      logo: 'https://via.placeholder.com/100?text=CreativeMinds',
      location: 'Remote',
    },
    description:
      'CreativeMinds is seeking a Laravel/Vue.js Developer to join our remote team. You will work on building modern, interactive web applications for our diverse client base.',
    requirements: [
      'At least 3 years of experience with Laravel and PHP',
      'Strong knowledge of Vue.js and modern JavaScript',
      'Experience with RESTful APIs and SPA development',
      'Understanding of responsive design principles',
      'Familiarity with version control systems like Git',
      'Good communication skills for remote collaboration',
    ],
    responsibilities: [
      'Develop web applications using Laravel and Vue.js',
      'Create reusable Vue components and implement complex UI features',
      'Build and consume RESTful APIs',
      'Ensure cross-browser compatibility and responsive design',
      'Participate in code reviews and provide feedback to other developers',
      'Collaborate with the design team to implement UI/UX improvements',
    ],
    location: 'Remote',
    locationType: 'remote',
    employmentType: 'contract',
    experienceLevel: 'mid',
    salaryRange: {
      min: 60,
      max: 80,
      currency: 'USD',
    },
    skills: [
      'Laravel',
      'PHP',
      'Vue.js',
      'JavaScript',
      'RESTful APIs',
      'SPA Development',
    ],
    postedDate: '2023-09-01',
    applicationUrl:
      'https://creativeminds.example.com/careers/laravel-vuejs-developer',
    featured: true,
  },
  {
    id: '5',
    title: 'Junior Laravel Developer',
    company: {
      name: 'StartupHub',
      logo: 'https://via.placeholder.com/100?text=StartupHub',
      location: 'Chicago, IL',
    },
    description:
      'StartupHub is looking for a Junior Laravel Developer to join our growing team. This is a great opportunity for someone who wants to grow their skills while working on real-world projects.',
    requirements: [
      'At least 1 year of experience with PHP and Laravel',
      'Basic knowledge of front-end technologies (HTML, CSS, JavaScript)',
      'Understanding of MVC architecture',
      'Familiarity with database systems like MySQL',
      'Eagerness to learn and grow as a developer',
      'Good problem-solving skills',
    ],
    responsibilities: [
      'Develop and maintain web applications using Laravel',
      'Implement features according to specifications',
      'Write clean, maintainable code',
      'Fix bugs and troubleshoot issues',
      'Collaborate with senior developers to learn best practices',
      'Assist with database design and optimization',
    ],
    location: 'Chicago, IL',
    locationType: 'hybrid',
    employmentType: 'full-time',
    experienceLevel: 'entry',
    salaryRange: {
      min: 65000,
      max: 80000,
      currency: 'USD',
    },
    skills: ['Laravel', 'PHP', 'MySQL', 'HTML/CSS', 'JavaScript', 'Git'],
    postedDate: '2023-08-25',
    applicationUrl:
      'https://startuphub.example.com/careers/junior-laravel-developer',
    featured: false,
  },
  {
    id: '6',
    title: 'Laravel Technical Lead',
    company: {
      name: 'EnterpriseApps',
      logo: 'https://via.placeholder.com/100?text=EnterpriseApps',
      location: 'Seattle, WA',
    },
    description:
      'EnterpriseApps is seeking a Laravel Technical Lead to oversee the development of our enterprise applications. You will lead a team of developers, make architectural decisions, and ensure the quality of our products.',
    requirements: [
      'At least 7 years of experience with PHP and Laravel',
      'Previous experience in a technical leadership role',
      'Strong knowledge of software architecture and design patterns',
      'Experience with cloud infrastructure (AWS, Azure, or GCP)',
      'Understanding of CI/CD pipelines and DevOps practices',
      'Excellent communication and leadership skills',
    ],
    responsibilities: [
      'Lead a team of Laravel developers and provide technical guidance',
      'Make architectural decisions and establish coding standards',
      'Review code and ensure code quality',
      'Work with product managers to define technical requirements',
      'Mentor junior developers and help them grow their skills',
      'Contribute to the development of key features',
    ],
    location: 'Seattle, WA',
    locationType: 'onsite',
    employmentType: 'full-time',
    experienceLevel: 'lead',
    salaryRange: {
      min: 140000,
      max: 180000,
      currency: 'USD',
    },
    skills: [
      'Laravel',
      'PHP',
      'Software Architecture',
      'Team Leadership',
      'DevOps',
      'Cloud Infrastructure',
    ],
    postedDate: '2023-08-20',
    applicationUrl:
      'https://enterpriseapps.example.com/careers/laravel-technical-lead',
    featured: true,
  },
  {
    id: '7',
    title: 'Laravel Freelance Developer',
    company: {
      name: 'DigitalAgency',
      logo: 'https://via.placeholder.com/100?text=DigitalAgency',
      location: 'Remote',
    },
    description:
      'DigitalAgency is looking for a Laravel Freelance Developer to work on various client projects. This is a flexible, project-based role ideal for experienced developers who prefer freelance work.',
    requirements: [
      'At least 3 years of experience with Laravel and PHP',
      'Portfolio of previous Laravel projects',
      'Strong communication skills',
      'Ability to work independently and manage your time effectively',
      'Experience with front-end technologies (HTML, CSS, JavaScript)',
      'Familiarity with version control systems',
    ],
    responsibilities: [
      'Develop web applications using Laravel for various clients',
      'Implement features according to client specifications',
      'Troubleshoot and fix issues in existing applications',
      'Communicate regularly with the project manager',
      'Provide time estimates for tasks',
      'Deliver high-quality code within deadlines',
    ],
    location: 'Remote',
    locationType: 'remote',
    employmentType: 'freelance',
    experienceLevel: 'mid',
    salaryRange: {
      min: 50,
      max: 70,
      currency: 'USD',
    },
    skills: ['Laravel', 'PHP', 'MySQL', 'HTML/CSS', 'JavaScript', 'Git'],
    postedDate: '2023-08-15',
    applicationUrl:
      'https://digitalagency.example.com/careers/laravel-freelance-developer',
    featured: false,
  },
  {
    id: '8',
    title: 'Laravel/React Developer',
    company: {
      name: 'ModernWeb',
      logo: 'https://via.placeholder.com/100?text=ModernWeb',
      location: 'Denver, CO',
    },
    description:
      'ModernWeb is seeking a Laravel/React Developer to help build modern, interactive web applications. You will work on both the back-end and front-end, creating RESTful APIs with Laravel and building user interfaces with React.',
    requirements: [
      'At least 3 years of experience with Laravel and PHP',
      'Strong knowledge of React and modern JavaScript',
      'Experience with RESTful API development',
      'Understanding of state management in React (Redux, Context API)',
      'Familiarity with CSS preprocessors like SASS or LESS',
      'Knowledge of responsive design principles',
    ],
    responsibilities: [
      'Develop web applications using Laravel and React',
      'Create RESTful APIs to support front-end functionality',
      'Build reusable React components',
      'Ensure cross-browser compatibility and responsive design',
      'Optimize applications for performance',
      'Collaborate with the design team to implement UI/UX features',
    ],
    location: 'Denver, CO',
    locationType: 'hybrid',
    employmentType: 'full-time',
    experienceLevel: 'mid',
    salaryRange: {
      min: 95000,
      max: 125000,
      currency: 'USD',
    },
    skills: [
      'Laravel',
      'PHP',
      'React',
      'JavaScript',
      'RESTful APIs',
      'Redux',
      'SASS',
    ],
    postedDate: '2023-08-10',
    applicationUrl:
      'https://modernweb.example.com/careers/laravel-react-developer',
    featured: false,
  },
  {
    id: '9',
    title: 'Laravel E-commerce Developer',
    company: {
      name: 'ShopTech',
      logo: 'https://via.placeholder.com/100?text=ShopTech',
      location: 'Portland, OR',
    },
    description:
      'ShopTech is looking for a Laravel E-commerce Developer to help build and maintain our e-commerce platform. You will work on implementing payment gateways, shopping cart functionality, and other e-commerce features.',
    requirements: [
      'At least 3 years of experience with Laravel and PHP',
      'Previous experience with e-commerce development',
      'Knowledge of payment gateway integration (Stripe, PayPal, etc.)',
      'Understanding of security best practices for e-commerce',
      'Experience with inventory management systems',
      'Familiarity with order processing workflows',
    ],
    responsibilities: [
      'Develop and maintain our Laravel-based e-commerce platform',
      'Implement and integrate payment gateways',
      'Build shopping cart functionality and checkout processes',
      'Ensure the security of customer data and transactions',
      'Optimize the performance of the e-commerce platform',
      'Troubleshoot and fix issues related to orders and payments',
    ],
    location: 'Portland, OR',
    locationType: 'onsite',
    employmentType: 'full-time',
    experienceLevel: 'senior',
    salaryRange: {
      min: 110000,
      max: 140000,
      currency: 'USD',
    },
    skills: [
      'Laravel',
      'PHP',
      'E-commerce',
      'Payment Gateways',
      'Security',
      'MySQL',
    ],
    postedDate: '2023-08-05',
    applicationUrl:
      'https://shoptech.example.com/careers/laravel-ecommerce-developer',
    featured: true,
  },
  {
    id: '10',
    title: 'Laravel API Developer',
    company: {
      name: 'APIConnect',
      logo: 'https://via.placeholder.com/100?text=APIConnect',
      location: 'Boston, MA',
    },
    description:
      'APIConnect is seeking a Laravel API Developer to help build and maintain our API services. You will work on designing RESTful APIs, implementing authentication and authorization, and ensuring the performance and security of our API infrastructure.',
    requirements: [
      'At least 3 years of experience with Laravel and PHP',
      'Strong knowledge of RESTful API design principles',
      'Experience with API authentication (OAuth, JWT, etc.)',
      'Understanding of API documentation tools (Swagger, OpenAPI)',
      'Familiarity with API testing frameworks',
      'Knowledge of performance optimization for APIs',
    ],
    responsibilities: [
      'Design and implement RESTful APIs using Laravel',
      'Develop authentication and authorization systems for APIs',
      'Create comprehensive API documentation',
      'Write tests for API endpoints',
      'Monitor API performance and implement optimizations',
      'Collaborate with front-end developers to ensure smooth API integration',
    ],
    location: 'Boston, MA',
    locationType: 'hybrid',
    employmentType: 'full-time',
    experienceLevel: 'senior',
    salaryRange: {
      min: 115000,
      max: 145000,
      currency: 'USD',
    },
    skills: [
      'Laravel',
      'PHP',
      'RESTful APIs',
      'OAuth',
      'JWT',
      'API Documentation',
      'API Testing',
    ],
    postedDate: '2023-07-30',
    applicationUrl:
      'https://apiconnect.example.com/careers/laravel-api-developer',
    featured: false,
  },
]
